
import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Col, Row, Divider, Button, Modal, Typography, Card, Flex, theme, ConfigProvider, Skeleton, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import { InsuranceCarrierLogo } from 'Components/commonComponents/InsuranceCarrierLogo';
import { ChosenByClientBadge } from '@shared/components/ChosenByClientBadge';
import { ListCoverages } from './ListCoverages';
import { ConnectionTypeTag } from '@shared/components/ConnectionTypeTag';
import { BACKEND_URL_QUOTATION } from 'Config/routes';
import { formatCurrency } from 'Utils/numberUtils';
import { InconsistenciesAlert } from './Main/InsurancePackageCard/InconsistenciesAlert';
import { PaymentSkeleton } from './Main/InsurancePackageCard/PaymentSkeleton';
import { CoveragesSkeleton } from './Main/InsurancePackageCard/CoveragesSkeleton';
import { ProcessingBanner } from './Main/InsurancePackageCard/ProcessingBanner';
import { ActionsDropdown } from './Main/InsurancePackageCard/ActionsDropdown';
import { IssuesTag } from './Main/InsurancePackageCard/IssuesTag';
import { IssuesAlert } from './Main/InsurancePackageCard/IssuesAlert';
import { issuesConfig } from './Main/InsurancePackageCard/constants/issuesConfig';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/App/Router/routes';
import { QUOTATION_INSURANCE_CARRIER_STATE } from '@/shared/constants/quotationInsuranceCarrierStates';

const { Title, Paragraph, Text } = Typography;

// retorna el data con el paquete, tomando como valor el retornado como pago ANUAL
export const getPaymentData = (insurancePackage) => {
  // Filtrar la forma de pago | Primero se toma el primero, pero se prioriza el pago ANUAL
  const payments = insurancePackage.payments ?? [];
  let newPayment = payments.find((item) => item);
  const anualPayment = payments.find((item) => item.id === 'ANUAL');
  if (anualPayment) newPayment = anualPayment;
  return newPayment;
};

// Card pequeña que representa un paquete seleccionado
export const SmallInsuranceCard = ({ insurancePackage, onClose, insuranceCarrier, fluid = false }) => {
  const payment = getPaymentData(insurancePackage);

  return (
    <ChosenByClientBadge isChosenByClient={insurancePackage.client_preference}>
      <Card
        key={insurancePackage.id}
        size='small'
        title={<InsuranceCarrierLogo name={insuranceCarrier.name} logoUrl={insuranceCarrier.logo_url} size='sm' style={{ paddingLeft: 24 }} />}
        style={{
          width: fluid ? '100%' : '150px',
          height: fluid ? '100%' : undefined,
          textAlign: 'center',
        }}
        extra={onClose && (
          <Button
            size='small'
            type='text'
            icon={<FontAwesomeIcon icon={faXmark} />}
            onClick={() => onClose(insurancePackage)}
          />
        )}
      >
        <Flex justify='center' align='center' style={{ height: '2.4em' }}>
          <Paragraph ellipsis={{ rows: 2, tooltip: true }} style={{ fontSize: '0.8em' }}>{insurancePackage.package_detail}</Paragraph>
        </Flex>
        <Text strong>{formatCurrency(payment?.value)}</Text>
        <ConnectionTypeTag
          connectionType={insurancePackage.source_type}
          testMode={insurancePackage.test_mode}
          tooltip={(title) => `Cotizado por ${title}`}
          style={{ position: 'absolute', bottom: -8, right: -8 }}
        />
      </Card>
    </ChosenByClientBadge>
  );
};

SmallInsuranceCard.propTypes = {
  insurancePackage: PropTypes.shape({
    id: PropTypes.string,
    package_detail: PropTypes.string,
    client_preference: PropTypes.bool,
  }).isRequired,
  //onClose: PropTypes.func.isRequired,
};


// ===================================== ===================================== ===================================== ===================================== =====================================

const SectionDivider = ({ isListMode }) => {
  return (
    <Divider
      type={isListMode ? 'vertical' : 'horizontal'}
      style={{
        ...(
          isListMode
            ? { height: '100%', marginInline: '16px' }
            : { marginBlock: '16px' }
        )
      }}
    />
  );
};

const PrimaryText = ({ children, ...props }) => {
  const { token: themeToken } = theme.useToken();
  return (
    <Text {...props} style={{ color: themeToken.colorPrimary, ...(props.style || {}) }}>
      {children}
    </Text>
  );
};

// Card que representa los paquetes devueltos por las aseguradoras
const InsurancePackageCard = ({
  insurancePackage,
  coverages = [],
  assists = [],
  onSelect = () => { },
  isSelected = false,
  checkboxIsDisabled = false,
  insuranceCarrier,
  handleShowModalDetail,
  quotationId,
  readOnly,
  isListMode,
  userProfileId,
}) => {
  // const [opacity, setOpacity] = useState(0);
  const payment = getPaymentData(insurancePackage);

  const { token: themeToken } = theme.useToken();

  const navigate = useNavigate();

  const reloadWindow = () => {
    //window.location.href = ``;
    navigate(ROUTES.QUOTATIONS.CREATE);
  };

  const countDown = (error) => {
    let secondsToGo = 60;
    let message;
    let modal;
    let down = <p>Se cerrará automáticamente en {secondsToGo} segundos.</p>;
    if (error) {
      message = <p>Hubo un error al procesar la información, por favor intente más tarde.</p>;
      modal = Modal.error({
        title: 'Gracias por elegirnos!',
        content: React.createElement('div', {}, [message, down]),
        okText: 'Confirmar',
        cancelButtonProps: { ghost: true },
      });
    } else {
      message = <p>Tu solicitud ya fue generada, nuestro equipo se pondrá en contacto.</p>;
      modal = Modal.info({
        title: 'Gracias por elegirnos!',
        content: React.createElement('div', {}, [message, down]),
        okText: 'Confirmar',
        okButtonProps: {
          onClick: () => {
            clearTimeout(timeoutId);
            modal.destroy();
            reloadWindow();
          }
        },
        cancelButtonProps: { ghost: true },
      });
    }
    const timer = setInterval(() => {
      secondsToGo -= 1;
      down = <p>Se cerrará automáticamente en {secondsToGo} segundos.</p>;
      modal.update({
        content: React.createElement('div', {}, [message, down]),
      });
    }, 1000);
    const timeoutId = setTimeout(() => {
      clearInterval(timer);
      modal.destroy();
      if (!error) {
        reloadWindow();
      }
    }, secondsToGo * 1000);
  };

  const handdleSolicitarExternalButton = (event, insurancePackageId) => {
    event.preventDefault();
    let secondsToGo = 60;
    //send email
    const token = localStorage.getItem('token');
    axios({
      method: 'POST',
      url: `${BACKEND_URL_QUOTATION}${quotationId}/send/`,
      data: { emails: ['external', insurancePackageId].toString() },
      headers: {
        Authorization: `Token ${token}`,
      },
    }).then((response) => {
      countDown(false);
    }).catch(() => {
      countDown(true);
    });
  };

  // Si el valor de la cobertura es un número, lo mejor es formatearla a money format
  /*   const formatCoverageValue = (coverageValue) => {
      // Si es un Número, se debe formatear a moneda
      if (!Number.isNaN(Number(coverageValue))) return `$${formatNumber(coverageValue)}`;
  
      return coverageValue;
    }; */

  const external_session = localStorage.getItem('external_session') === "true" || false;

  const hasAlerts = insurancePackage.alerts?.length > 0;
  const hasErrors = insurancePackage.errors?.length > 0;
  const hasPackageCoverages = (insurancePackage.coverages?.length > 0) || (insurancePackage.assists?.length > 0) || (insurancePackage.other_coverages?.length > 0);
  
  const isProcessing = insurancePackage.state === QUOTATION_INSURANCE_CARRIER_STATE.PROCESSING;
  const showCoveragesSkeleton = isProcessing && !hasPackageCoverages;
  const showPlanSkeleton = isProcessing && !insurancePackage.package_detail?.trim();
  const showPaymentSkeleton = isProcessing && !payment;

  const disabledStyle = {
    filter: 'blur(2px)',
    pointerEvents: 'none',
    userSelect: 'none',
  };

  return (
    <ConfigProvider
      theme={{
        token:
        {
          // Se establece el color primario de la Card dependiendo si hay errores o alertas
          ...(hasErrors && { colorPrimary: issuesConfig['error'].color }),
          ...((!hasErrors && hasAlerts) && { colorPrimary: issuesConfig['alert'].color }),
        }
      }}
    >
      <ChosenByClientBadge isChosenByClient={insurancePackage.client_preference}>
        <Card
          hoverable
          style={{
            cursor: 'default', // Deshabilita el cursor de hover
            ...(isSelected && {
              backgroundColor: `${themeToken.colorPrimary}0a`, // 4% de opacidad
              borderColor: `${themeToken.colorPrimary}66`, // 40% de opacidad
            }),
            ...(hasErrors && { borderColor: issuesConfig['error'].color }),
            ...((!hasErrors && hasAlerts) && { borderColor: issuesConfig['alert'].color }),
            ...((hasErrors || hasAlerts) && { borderWidth: 2 }),
            height: '100%',
          }}
          styles={{ body: { height: '100%' } }}
        >
          <IssuesTag errors={insurancePackage.errors} alerts={insurancePackage.alerts} inconsistencies={insurancePackage.data_differences} />
          <ActionsDropdown record={insurancePackage} userProfileId={userProfileId} />
          <Flex vertical={!isListMode} align='center' style={{ height: '100%' }}>
            <Flex vertical gap={8} align='center' flex={1} style={{ textAlign: 'center', width: '100%' }}>
              {isProcessing && <ProcessingBanner />}
              <InsuranceCarrierLogo name={insuranceCarrier.name} logoUrl={insuranceCarrier.logo_url} size='lg' />
              <img src="https://res.cloudinary.com/compara/image/upload/v1569616070/assets/card/shadow.svg" alt='Shadow decoration below insurance carrier logo' height={11} />
              {showPlanSkeleton ? (
                <Skeleton.Input active style={{ width: '9em', minWidth: 0, height: '1.2em' }} />
              ) : insurancePackage.package_detail ? (
                <Title level={5} ellipsis={{ rows: 2, tooltip: true }} style={{ margin: 0 }}>{insurancePackage.package_detail}</Title>
              ) : null}
              <IssuesAlert type='error' items={insurancePackage.errors} />
              <IssuesAlert type='alert' items={insurancePackage.alerts} />
              <InconsistenciesAlert items={insurancePackage.data_differences} />
            </Flex>
            <SectionDivider isListMode={isListMode} />
            <Flex
              flex={1}
              justify='center'
              style={{
                width: '100%',
                ...((hasErrors || hasAlerts) && disabledStyle)
              }}
            >
              {showCoveragesSkeleton ? (
                <CoveragesSkeleton />
              ) : (
                <ListCoverages showAll={false} insurancePackage={insurancePackage} coverages={coverages} assists={assists} />
              )}
            </Flex>
            <SectionDivider isListMode={isListMode} />
            <Flex
              vertical
              align='center'
              gap={8}
              flex={1}
              style={{
                textAlign: 'center',
                ...((hasErrors || hasAlerts) && disabledStyle)
              }}
            >
              <Flex vertical gap={4}>
                {payment && !showPaymentSkeleton ? (
                  <PrimaryText style={{ fontSize: '1.8em', fontWeight: 700, lineHeight: 1 }}>{formatCurrency(payment.value)}</PrimaryText>
                ) : (
                  <PaymentSkeleton active={isProcessing} />
                )}
                <Text type='secondary' style={{ fontSize: '0.9em', lineHeight: 1 }}>Prima anual</Text>
              </Flex>
              <Row gutter={[8, 4]}>
                <Col flex='1'>
                  <Tooltip title={isProcessing ? 'Podrás ver más detalles cuando finalice el procesamiento.' : null} mouseEnterDelay={0.3}>
                    <Button
                      color='primary'
                      variant='outlined'
                      block
                      disabled={isProcessing}
                      onClick={() => handleShowModalDetail(insurancePackage.id)}
                    >
                      Más detalles
                    </Button>
                  </Tooltip>
                </Col>
                {!readOnly && (
                  <Col flex='1'>
                    <Tooltip title={isProcessing ? 'Podrás seleccionar el plan cuando finalice el procesamiento.' : null} mouseEnterDelay={0.3}>
                      <Button
                        type='primary'
                        onClick={() => onSelect(!isSelected, insurancePackage)}
                        disabled={isProcessing || (checkboxIsDisabled && !isSelected)}
                        block
                      >
                        {external_session
                          ? 'Comparar'
                          : isSelected ? 'Deseleccionar' : 'Seleccionar'
                        }
                      </Button>
                    </Tooltip>
                  </Col>
                )}
                <Col span={24}>
                  <Text type='secondary' style={{ fontSize: '0.9em' }}>Cotización válida por {insurancePackage.validity_days} días</Text>
                </Col>
                {external_session && !readOnly && (
                  <Col span={24}>
                    <Button
                      variant='text'
                      color='primary'
                      onClick={(e) => handdleSolicitarExternalButton(e, insurancePackage.id)}
                      disabled={isProcessing}
                      block
                      icon={<FontAwesomeIcon icon={faPaperPlane} />}
                    >
                      Solicitar
                    </Button>
                  </Col>
                )}
              </Row>
            </Flex>
          </Flex>
          <ConnectionTypeTag
            connectionType={insurancePackage.source_type}
            testMode={insurancePackage.test_mode}
            tooltip={(title) => `Cotizado por ${title}`}
            style={{ position: 'absolute', bottom: -8, right: -8 }}
          />
        </Card>
      </ChosenByClientBadge>
    </ConfigProvider>
  );
};

InsurancePackageCard.propTypes = {
  coverages: PropTypes.array,
  assists: PropTypes.array,
  insurancePackage: PropTypes.shape({
    id: PropTypes.string,
    package_detail: PropTypes.string,
    state: PropTypes.string,
    coverages: PropTypes.array,
    assists: PropTypes.array,
    payments: PropTypes.array,
  }).isRequired,
  onSelect: PropTypes.func,
  isSelected: PropTypes.bool,
  checkboxIsDisabled: PropTypes.bool,
  handleShowModalDetail: PropTypes.func,
  quotationId: PropTypes.string,
};

export default InsurancePackageCard;
